import React, { useEffect, useMemo, useState } from "react";
import { Add, Delete, Edit, Save } from "@mui/icons-material";
import { createUser, deleteUser, fetchAgencies, fetchRoles, fetchUsers, updateUser } from "../services/owdedApi";
import { EmptyState, Modal, StatusPill } from "./ui";
import { BASE_URL } from "../services/constant";
import { useAuth } from "../context/AuthContext";

const initialForm = {
  last_name: "",
  first_name: "",
  sex: "M",
  address: "",
  email: "",
  phone: "",
  password: "",
  role_code: "AGENT_CREDIT",    
  agency_id: "",
  photo_url: "",
  photo: null,
  status: "ACTIVE",
};

function UsersPage() {
  const { user: connectedUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [agencies, setAgencies] = useState([]);
  const [form, setForm] = useState(initialForm);
  const [search, setSearch] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [notice, setNotice] = useState("");

  const load = async () => {
    const [userRows, roleRows, agencyRows] = await Promise.all([
      fetchUsers(),
      fetchRoles(),
      fetchAgencies().catch(() => []),
    ]);
    setUsers(userRows);
    setRoles(roleRows);
    setAgencies(agencyRows);
  };

  useEffect(() => {
    load().catch(() => setNotice("Impossible de charger les utilisateurs."));
  }, []);

  const filtered = useMemo(() => {
    const term = search.toLowerCase();
    return users.filter((user) =>
      [user.full_name, user.email, user.phone, user.role_label, user.agency_name]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(term))
    );
  }, [users, search]);
  const isDirectorGeneral = connectedUser?.role_code === "DIRECTEUR_GENERAL";

  const openCreate = () => {
    setEditing(null);
    setForm(initialForm);
    setModalOpen(true);
  };

  const openEdit = (user) => {
    setEditing(user);
    setForm({ ...initialForm, ...user, password: "", photo: null });
    setModalOpen(true);
  };

  const assetUrl = (url) => {
    if (!url) return "";
    return url.startsWith("http") ? url : `${BASE_URL}${url}`;
  };

  const toFormData = () => {
    const data = new FormData();
    const payload = {
      ...form,
      agency_id: form.agency_id || "",
      full_name: `${form.last_name} ${form.first_name}`.trim(),
    };
    if (!payload.password) delete payload.password;
    delete payload.photo;

    Object.entries(payload).forEach(([key, value]) => {
      if (value !== undefined && value !== null) data.append(key, value);
    });
    if (form.photo) data.append("photo", form.photo);
    return data;
  };

  const submit = async (event) => {
    event.preventDefault();
    const payload = toFormData();

    if (editing) {
      await updateUser(editing.id, payload);
      setNotice("Utilisateur modifie.");
    } else {
      await createUser(payload);
      setNotice("Utilisateur cree.");
    }
    setModalOpen(false);
    await load();
  };

  const remove = async (user) => {
    if (!window.confirm(`Supprimer ${user.full_name} ?`)) return;
    await deleteUser(user.id);
    setNotice("Utilisateur supprime logiquement.");
    await load();
  };

  return (
    <main className="page-content management-page">
      <div className="page-head">
        <div>
          <h1>Utilisateurs</h1>
          <p>Agents de credit, fonctions utilisateur, agences et acces.</p>
        </div>
      </div>
      {notice && <p className="notice">{notice}</p>}

      <section className="panel data-panel">
        <div className="table-toolbar">
          <input placeholder="Recherche avancee..." value={search} onChange={(event) => setSearch(event.target.value)} />
          <button className="primary-btn" type="button" onClick={openCreate}><Add /> Ajouter</button>
        </div>
        {filtered.length ? (
          <table className="data-table">
            <thead><tr><th>ID</th><th>Agent</th><th>Contact</th><th>Fonction</th><th>Agence</th><th>Statut</th><th>Actions</th></tr></thead>
            <tbody>{filtered.map((user) => (
              <tr key={user.id}>
                <td>{user.id}</td>
                <td>
                  <div className="user-table-cell">
                    {user.photo_url ? <img src={assetUrl(user.photo_url)} alt={user.full_name} /> : <span>{String(user.full_name || "?").slice(0, 1)}</span>}
                    <div>{user.full_name}<br /><small>{user.sex} - {user.address}</small></div>
                  </div>
                </td>
                <td>{user.email}<br /><small>{user.phone}</small></td>
                <td>{user.role_label}</td>
                <td>{user.agency_name}</td>
                <td><StatusPill value={user.status} /></td>
                <td>
                  <div className="table-actions">
                    <button className="icon-action" type="button" onClick={() => openEdit(user)} aria-label="Modifier"><Edit /></button>
                    {isDirectorGeneral && <button className="icon-action danger" type="button" onClick={() => remove(user)} aria-label="Supprimer"><Delete /></button>}
                  </div>
                </td>
              </tr>
            ))}</tbody>
          </table>
        ) : <EmptyState />}
      </section>

      {modalOpen && (
        <Modal
          title={editing ? "Modifier l'agent" : "Ajouter un agent"}
          onClose={() => setModalOpen(false)}
          footer={<button className="primary-btn" form="user-form"><Save /> Enregistrer</button>}
        >
          <form id="user-form" onSubmit={submit}>
            <div className="form-grid">
              <label>Nom<input value={form.last_name || ""} onChange={(e) => setForm({ ...form, last_name: e.target.value })} required /></label>
              <label>Prenom<input value={form.first_name || ""} onChange={(e) => setForm({ ...form, first_name: e.target.value })} required /></label>
              <label>Sexe<select value={form.sex || "M"} onChange={(e) => setForm({ ...form, sex: e.target.value })}><option value="M">Masculin</option><option value="F">Feminin</option><option value="AUTRE">Autre</option></select></label>
              <label>Telephone<input value={form.phone || ""} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></label>
              <label>Email<input type="email" value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.target.value })} required /></label>
              <label>Mot de passe<input type="password" value={form.password || ""} onChange={(e) => setForm({ ...form, password: e.target.value })} required={!editing} /></label>
              <label>Fonction<select value={form.role_code || ""} onChange={(e) => setForm({ ...form, role_code: e.target.value })} required>{roles.map((role) => <option value={role.code} key={role.code}>{role.name}</option>)}</select></label>
              <label>Agence<select value={form.agency_id || ""} onChange={(e) => setForm({ ...form, agency_id: e.target.value })}><option value="">Aucune</option>{agencies.map((agency) => <option value={agency.id} key={agency.id}>{agency.name}</option>)}</select></label>
              <label className="full-row">Adresse<input value={form.address || ""} onChange={(e) => setForm({ ...form, address: e.target.value })} /></label>
              <label className="full-row">Photo utilisateur<input type="file" accept="image/*" onChange={(e) => setForm({ ...form, photo: e.target.files?.[0] || null })} /></label>
              {form.photo_url && !form.photo && <div className="full-row form-image-preview"><img src={assetUrl(form.photo_url)} alt="Utilisateur" /><span>Photo actuelle</span></div>}
            </div>
          </form>
        </Modal>
      )}
    </main>
  );
}

export default UsersPage;
